import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import { EditOutlined as EditIcon } from "@mui/icons-material";
import RichContent from "./RichContent";
import ContenidoNavegable from "./ContenidoNavegable";
import EditorTexto from "../kit/EditorTexto";
import { useActualizarBloque, useBloque } from "../../queries/useBloque";
import { useAuthStore } from "../../stores/authStore";
import { mensajeDeError } from "../../utils/errores";

/**
 * Contenido de un bloque editable del admin (inicio, preguntas, coordinadores),
 * **sin contenedor**: el ancho y los márgenes los pone la página, que no son los
 * mismos en el sitio público que dentro de la app.
 *
 * Lo comparten ambos lados: la información es la misma con sesión y sin ella, y
 * quien ya entró a la app también necesita leerla.
 *
 * `alPie` es para un bloque de cierre (los coordinadores bajo las preguntas): va
 * tras un separador y, si no existe, no aparece —ni su carga ni su error, que
 * distraerían del contenido principal—.
 *
 * `editable` ofrece el botón "Editar" al coordinador, para cambiar el texto sin
 * pasar por el admin de Django. Lo piden las páginas de la app; el sitio público
 * se deja de solo lectura aunque quien mire tenga sesión de coordinación.
 *
 * `conIndice` es para los bloques largos (las preguntas): índice al lado y
 * secciones plegables. Si el contenido no tiene títulos que indexar, se pinta
 * igual que siempre, así que la página no tiene que adivinar cuánto texto hay.
 */
export default function BloqueVista({
  clave,
  alPie = false,
  editable = false,
  conIndice = false,
}: {
  clave: string;
  alPie?: boolean;
  editable?: boolean;
  conIndice?: boolean;
}) {
  const { user } = useAuthStore();
  const { data, isLoading, isError } = useBloque(clave);
  const [editando, setEditando] = useState(false);

  const puedeEditar = editable && !!user?.es_coordinador_global;
  const editor = puedeEditar && editando && (
    <EditarBloqueDialog clave={clave} contenido={data?.contenido ?? ""} onCerrar={() => setEditando(false)} />
  );
  const boton = puedeEditar ? <BotonEditar onPulsar={() => setEditando(true)} /> : null;

  if (alPie) {
    // Sin contenido no hay nada que separar; con permiso de edición igual se
    // muestra el botón, o un bloque vacío no habría cómo estrenarlo.
    if (!data && !puedeEditar) return null;
    return (
      <>
        <Divider sx={{ my: 5 }} />
        {boton}
        {data && <RichContent html={data.contenido} />}
        {editor}
      </>
    );
  }

  return (
    <>
      {/* Con índice, el botón viaja dentro para quedar sobre la columna de lectura. */}
      {(!conIndice || !data) && boton}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar el contenido.</Alert>}
      {data &&
        (conIndice ? (
          <ContenidoNavegable html={data.contenido} accion={boton} />
        ) : (
          <RichContent html={data.contenido} />
        ))}
      {editor}
    </>
  );
}

/** Alineado a la derecha, sobre el texto: acompaña sin competir con el contenido. */
function BotonEditar({ onPulsar }: { onPulsar: () => void }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onPulsar}>
        Editar contenido
      </Button>
    </Box>
  );
}

/**
 * Edición del bloque en un diálogo ancho: son páginas largas, y a media pantalla
 * no se ve lo que se está escribiendo. Se guarda al confirmar, no mientras se
 * teclea: es contenido público y conviene un paso explícito.
 */
function EditarBloqueDialog({ clave, contenido, onCerrar }: { clave: string; contenido: string; onCerrar: () => void }) {
  const guardar = useActualizarBloque(clave);
  const [html, setHtml] = useState(contenido);

  return (
    <Dialog open onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogTitle>Editar contenido</DialogTitle>
      <DialogContent>
        {guardar.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mensajeDeError(guardar.error, "No se pudo guardar el contenido.")}
          </Alert>
        )}
        <EditorTexto
          value={html}
          onChange={setHtml}
          conTitulos
          minHeight={380}
          // El texto scrollea dentro de la caja, no el diálogo: así la barra de
          // herramientas no se va con el scroll y el editor ocupa lo que hay.
          maxHeight="calc(100vh - 300px)"
          disabled={guardar.isPending}
          placeholder="Escribe el contenido de la página…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" disabled={guardar.isPending} onClick={() => guardar.mutate(html, { onSuccess: onCerrar })}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
